<script setup lang="ts">
import { useControlId } from '../../composables/useControlId'
import { computed, ref } from 'vue'
import { AlertCircle } from '@lucide/vue'
import RrButton from '../button/RrButton.vue'
import RenderContent from '../internal/RenderContent'
import { usePopup, type PopupPlacement } from '../../composables/usePopup'
import { useBoundValue } from '../../composables/useBoundValue'
import { useFormControl } from '../../composables/useFormControl'
import type { VNodeChild } from 'vue'
import type { ButtonType } from '../../types'

type ButtonProps = InstanceType<typeof RrButton>['$props']
defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  title?: VNodeChild
  description?: VNodeChild
  open?: boolean
  defaultOpen?: boolean
  placement?: PopupPlacement
  okText?: string
  cancelText?: string
  okType?: ButtonType
  disabled?: boolean
  confirmLoading?: boolean
  showCancel?: boolean
  okButtonProps?: ButtonProps
  cancelButtonProps?: ButtonProps
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement
  onConfirm?: (event: MouseEvent) => void | Promise<unknown>
}>(), { title: '确定执行此操作吗？', open: undefined, defaultOpen: false, placement: 'top', okType: 'primary', disabled: undefined, confirmLoading: false, showCancel: true })
const emit = defineEmits<{ 'update:open': [open: boolean]; openChange: [open: boolean]; cancel: [event: MouseEvent] }>()
const pending = ref(false)
const { disabled, config } = useFormControl(props)
const innerOpen = ref(props.defaultOpen)
const mergedOpen = useBoundValue(props, 'open', innerOpen)
const visible = computed(() => !disabled.value && Boolean(mergedOpen.value))
const popRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const titleId = `rr-popconfirm-${useControlId()}`
function setOpen(value: boolean) {
  if ((disabled.value && value) || value === Boolean(mergedOpen.value)) return
  innerOpen.value = value
  emit('update:open', value)
  emit('openChange', value)
}
const focusTrigger = () => popRef.value?.querySelector<HTMLElement>('button,a,[tabindex]')?.focus({ preventScroll: true })
const { container, style } = usePopup(visible, popRef, panelRef, { placement: () => props.placement, getPopupContainer: () => props.getPopupContainer, close: () => setOpen(false), onEscape: focusTrigger, autoFocus: true })
async function handleConfirm(event: MouseEvent) {
  if (pending.value || props.confirmLoading || props.okButtonProps?.loading || props.okButtonProps?.disabled) return
  pending.value = true
  try {
    await props.onConfirm?.(event)
    setOpen(false)
    focusTrigger()
  } finally {
    pending.value = false
  }
}
function handleCancel(event: MouseEvent) { emit('cancel', event); setOpen(false); focusTrigger() }
</script>

<template>
  <span ref="popRef" v-bind="$attrs" class="rr-popconfirm-wrapper"><span class="rr-popconfirm-trigger" @click.stop="setOpen(!visible)"><slot /></span></span>
  <Teleport v-if="container" :to="container">
    <Transition name="rr-slide-up"><div v-if="visible" ref="panelRef" class="rr-popconfirm" :style="style" role="dialog" :aria-labelledby="titleId" tabindex="-1" @click.stop>
      <div class="rr-popconfirm__content"><slot name="icon"><AlertCircle :size="16" class="rr-popconfirm__icon" aria-hidden="true" /></slot><div class="rr-popconfirm__message"><div :id="titleId" class="rr-popconfirm__title"><slot name="title"><RenderContent :value="title" /></slot></div><div v-if="description || $slots.description" class="rr-popconfirm__description"><slot name="description"><RenderContent :value="description" /></slot></div></div></div>
      <div class="rr-popconfirm__buttons"><RrButton v-if="showCancel" size="small" type="default" v-bind="cancelButtonProps" @click="handleCancel">{{ cancelText ?? config.locale?.Popconfirm?.cancelText ?? '取消' }}</RrButton><RrButton size="small" :type="okType" v-bind="okButtonProps" :loading="pending || confirmLoading || okButtonProps?.loading" @click="handleConfirm">{{ okText ?? config.locale?.Popconfirm?.okText ?? '确定' }}</RrButton></div>
    </div></Transition>
  </Teleport>
</template>

<style scoped>
.rr-popconfirm-wrapper {
  position: relative;
  display: inline-flex;
}

.rr-popconfirm-trigger {
  display: inline-flex;
  cursor: pointer;
}

.rr-popconfirm {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--rr-z-command, 850);
  min-width: 200px;
  max-width: 320px;
  padding: 12px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-overlay);
  color: var(--rr-color-text);
  font-size: 13px;
}

.rr-popconfirm__content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.rr-popconfirm__icon {
  color: var(--rr-color-warning);
  flex-shrink: 0;
  margin-top: 1px;
}

.rr-popconfirm__message {
  flex: 1 1 auto;
}

.rr-popconfirm__title {
  font-weight: 600;
  line-height: 1.3;
}

.rr-popconfirm__description {
  margin-top: 4px;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.rr-popconfirm__buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 10px;
}
</style>
